import classNames from 'classnames';
import { useRef, useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import ReCAPTCHA from 'react-google-recaptcha';
import { FieldTextInput, PrimaryButton } from '../../../../components';
import { useIntl } from '../../../../util/reactIntl';
import * as validators from '../../../../util/validators';
import { sendEmail } from '../../../../util/api';
import css from './ContactForm.module.css';

const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const ContactFormComponent = props => {
  const [loader, setLoader] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaError, setCaptchaError] = useState(false);
  const recaptchaRef = useRef(null);
  const intl = useIntl();

  const {
    rootClassName,
    className,
    submitButtonText,
    placeholderButtonText,
    title,
    description,
    ...restProps
  } = props;

  const handleSubmit = async values => {
    // Validate reCAPTCHA
    if (!captchaToken) {
      setCaptchaError(true);
      return;
    }
    setCaptchaError(false);

    try {
      setLoader(true);
      const { name, email, inquiry } = values;

      const htmlContent = `
    <p><b>Name</b> ${name.trim()}</p>
    <p><b>Email:</b> ${email.trim()}</p>
    <p><b>Inquiry:</b> ${inquiry.trim()}</p>
  `;

      await sendEmail({
        subject: 'New inquiry via contact us page',
        htmlContent,
        captchaToken,
      });

      window.alert('Message sent successfully');

      // Reset reCAPTCHA after successful submission
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setCaptchaToken(null);
      }
    } catch (error) {
      console.log('Error sending email:', error);
    } finally {
      setLoader(false);
    }
  };

  const handleCaptchaChange = token => {
    setCaptchaToken(token);
    if (token) {
      setCaptchaError(false);
    }
  };

  const handleCaptchaExpired = () => {
    setCaptchaToken(null);
  };

  const classes = classNames(rootClassName || css.root, className);

  return (
    <FinalForm
      {...restProps}
      onSubmit={handleSubmit}
      render={fieldRenderProps => {
        const {
          handleSubmit: formHandleSubmit,
          invalid,
          pristine,
          submitting,
          form,
        } = fieldRenderProps;

        const submitDisabled = invalid || pristine || submitting || loader || !captchaToken;

        const nameRequired = validators.required(
          intl.formatMessage({ id: 'ContactForm.nameRequired' })
        );
        const emailRequired = validators.required(
          intl.formatMessage({ id: 'ContactForm.emailRequired' })
        );
        const emailValid = validators.emailFormatValid(
          intl.formatMessage({ id: 'ContactForm.emailInvalid' })
        );
        const inquiryRequired = validators.required(
          intl.formatMessage({ id: 'ContactForm.inquiryRequired' })
        );

        return (
          <form
            className={classes}
            onSubmit={e => {
              e.preventDefault();
              formHandleSubmit(e, form);
            }}
          >
            <div className={css.formHeader}>
              <h2 className={css.title}>{title.content}</h2>
              <p className={css.description}>{description.content}</p>
            </div>

            <div className={css.formFields}>
              <FieldTextInput
                className={css.field}
                type="text"
                id="contactForm.name"
                name="name"
                label={intl.formatMessage({ id: 'ContactForm.nameLabel' })}
                placeholder={intl.formatMessage({ id: 'ContactForm.namePlaceholder' })}
                validate={nameRequired}
              />

              <FieldTextInput
                className={css.field}
                type="email"
                id="contactForm.email"
                name="email"
                label={intl.formatMessage({ id: 'ContactForm.emailLabel' })}
                placeholder={intl.formatMessage({ id: 'ContactForm.emailPlaceholder' })}
                validate={validators.composeValidators(emailRequired, emailValid)}
              />

              <FieldTextInput
                className={css.field}
                type="textarea"
                id="contactForm.inquiry"
                name="inquiry"
                label={intl.formatMessage({ id: 'ContactForm.messageLabel' })}
                placeholder={intl.formatMessage({ id: 'ContactForm.messagePlaceholder' })}
                validate={inquiryRequired}
              />
            </div>

            <div className={css.submitContainer}>
              <div className={css.recaptchaContainer}>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={siteKey}
                  onChange={handleCaptchaChange}
                  onExpired={handleCaptchaExpired}
                />
                {captchaError && (
                  <p className={css.recaptchaError}>
                    {intl.formatMessage({ id: 'ContactForm.recaptchaRequired' })}
                  </p>
                )}
              </div>

              <PrimaryButton
                type="submit"
                className={css.submitButton}
                inProgress={loader || submitting}
                disabled={submitDisabled}
              >
                {loader || submitting
                  ? placeholderButtonText || intl.formatMessage({ id: 'ContactForm.sending' })
                  : submitButtonText || intl.formatMessage({ id: 'ContactForm.sendMessage' })}
              </PrimaryButton>
            </div>
          </form>
        );
      }}
    />
  );
};

/**
 * Contact form component for collecting user inquiries and support questions
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.submitButtonText custom text for submit button
 * @param {string?} props.placeholderButtonText custom text for submit button when in progress
 * @returns {JSX.Element} contact form component
 */
const ContactForm = props => <ContactFormComponent {...props} />;

export default ContactForm;
